import { Button, Modal, Label, TextInput } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { District } from "@/sharedTypes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PaginatedSelect from "@/components/PaginatedSelect";
import { useGetCitiesQuery } from "@/api/locationApi/city";

interface DistrictFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: District) => void;
  defaultValues?: District;
  mode?: "create" | "edit";
  errorMessage?: string;
}

export default function DistrictFormDialog({
  open,
  onClose,
  onSubmit,
  defaultValues,
  mode = "create",
  errorMessage,
}: DistrictFormDialogProps) {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<District>();

  const [cityPage, setCityPage] = useState(1);
  const [citySearch, setCitySearch] = useState("");
  const { data: citiesData, isFetching: isCitiesFetching } = useGetCitiesQuery({
    pagination: { page: cityPage, pageSize: 15 },
    filters: citySearch ? { "city_name[like]": citySearch } : {},
  });
  const cityOptions = (citiesData?.items || []).map((c: any) => ({
    label: c.city_name,
    value: String(c.id),
  }));

  useEffect(() => {
    if (open) {
      reset(
        defaultValues || {
          district_name: "",
          city_id: "",
        }
      );
      setCitySearch("");
      setCityPage(1);
    }
  }, [open, defaultValues, reset]);

  return (
    <Modal show={open} onClose={onClose} size="lg">
      <Modal.Header>
        {mode === "edit" ? t("temporalActivity:district.edit") : t("temporalActivity:district.create")}
      </Modal.Header>
      <Modal.Body className="overflow-visible">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <div className="mb-2 block">
              <Label htmlFor="district_name" value={t("temporalActivity:district.name")} />
            </div>
            <TextInput
              id="district_name"
              placeholder={t("temporalActivity:district.namePlaceholder")}
              {...register("district_name", {
                required: t("temporalActivity:district.nameRequired") as string,
              })}
              color={errors.district_name ? "failure" : "gray"}
              helperText={errors.district_name?.message}
            />
          </div>

          <Controller
            name="city_id"
            control={control}
            rules={{ required: t("temporalActivity:district.cityRequired") as string }}
            render={({ field }) => {
              const selected = field.value
                ? {
                    label:
                      cityOptions.find((o: any) => o.value === String(field.value))?.label ??
                      defaultValues?.city?.city_name ??
                      "",
                    value: String(field.value),
                  }
                : null;
              return (
                <PaginatedSelect
                  id="city_id"
                  label={t("temporalActivity:district.city")}
                  value={selected}
                  onChange={(val: any) => field.onChange(val?.value ?? "")}
                  options={cityOptions}
                  onInputChange={(val) => {
                    setCitySearch(val);
                    setCityPage(1);
                  }}
                  isLoading={isCitiesFetching}
                  isFetching={isCitiesFetching}
                  placeholder={t("temporalActivity:district.selectCity")}
                  page={cityPage}
                  totalPages={citiesData?.pagination?.totalPages || 1}
                  onPageChange={setCityPage}
                />
              );
            }}
          />
          {errors.city_id && <p className="text-sm text-red-500">{errors.city_id.message}</p>}

          {errorMessage && <div className="text-sm text-red-500">{errorMessage}</div>}

          <div className="flex justify-end gap-x-2">
            <Button color="gray" onClick={onClose}>
              {t("common:cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {mode === "edit" ? t("common:save") : t("common:create")}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
